import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { Button, Input, Modal } from "@vv/ui";
import { useState } from "react";
import type { AssignPayload } from "../domain/request.types";
import { Priority } from "../domain/request.types";
import { PriorityChip } from "./PriorityChip";

export interface AssignModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: AssignPayload) => Promise<void>;
  /** Reused for `approveRework` (re-staffing after a rework claim) as well as `accept` - defaults to the accept-flow copy. */
  title?: string;
  confirmLabel?: string;
}

export function AssignModal({
  open,
  onClose,
  onConfirm,
  title = "Accept request",
  confirmLabel = "Accept",
}: AssignModalProps) {
  const [engineerIdsRaw, setEngineerIdsRaw] = useState("");
  const [priority, setPriority] = useState<Priority>(Priority.Medium);
  const [expectedCompletionDate, setExpectedCompletionDate] = useState("");
  const [saving, setSaving] = useState(false);

  const engineerIds = engineerIdsRaw
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

  const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const invalidIds = engineerIds.filter((id) => !UUID_PATTERN.test(id));

  const isValid =
    engineerIds.length > 0 && invalidIds.length === 0 && expectedCompletionDate !== "";

  async function handleConfirm() {
    setSaving(true);
    try {
      await onConfirm({ engineerIds, priority, expectedCompletionDate });
      setEngineerIdsRaw("");
      setExpectedCompletionDate("");
      setPriority(Priority.Medium);
    } catch {
      // Already toasted by the caller's runAction() - swallow here so it
      // doesn't also surface as an unhandled promise rejection. The form is
      // deliberately left as-is (not reset) so the user can fix and retry.
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" loading={saving} disabled={!isValid} onClick={handleConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <Stack spacing={2.5} sx={{ pt: 1 }}>
        <Input
          label="Engineer user ID(s)"
          value={engineerIdsRaw}
          onChange={(event) => setEngineerIdsRaw(event.target.value)}
          error={invalidIds.length > 0}
          helperText={
            invalidIds.length > 0
              ? `Not a valid user ID: ${invalidIds.join(", ")}`
              : "Comma-separated user IDs - no user directory yet"
          }
          required
          autoFocus
        />

        <Stack spacing={1}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Priority
          </Typography>
          <ToggleButtonGroup
            exclusive
            value={priority}
            onChange={(_event, value: Priority | null) => value && setPriority(value)}
          >
            {Object.values(Priority).map((value) => (
              <ToggleButton key={value} value={value} sx={{ p: 0.5 }}>
                <PriorityChip priority={value} />
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        <Input
          label="Expected completion date"
          type="date"
          value={expectedCompletionDate}
          onChange={(event) => setExpectedCompletionDate(event.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          required
        />
      </Stack>
    </Modal>
  );
}
